import { useEffect, useId, useLayoutEffect, useRef, useState, type PointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { daaluuKind, type DaaluuTile as Tile } from '@big2/shared';
import { DaaluuTile } from './DaaluuTile.js';

interface Props {
  hand: Tile[]; identity: string; mn: boolean; label: string;
  selected: string[]; deadTileIds: string[]; arriving: string[];
  selectionDisabled: boolean; onSelect?: (id: string) => void; onLayout?: () => void;
  onPlayDrop?: (id: string) => void; onTributeDrop?: (id: string, slaveSeat: number) => void; onPlayDragChange?: (id: string | null) => void;
  visible?: boolean; collecting?: boolean;
}
interface Drag {
  id: string; pointerId: number; element: HTMLElement; startX: number; startY: number;
  original: string[]; active: boolean;
}

function readSavedOrder(identity: string, hand: Tile[]): string[] {
  try {
    const saved: unknown = JSON.parse(window.localStorage.getItem(`daaluu-hand-order:${identity}`) ?? 'null');
    if (Array.isArray(saved) && saved.every(id => typeof id === 'string')) return saved;
  } catch { /* Storage may be unavailable; use the current hand order. */ }
  return hand.map(tile => tile.id);
}

/** Manual presentation order only. Never sorts tiles or submits a game action. */
export function DaaluuHand({ hand, identity, mn, label, selected, deadTileIds, arriving, selectionDisabled, onSelect, onLayout, onPlayDrop, onTributeDrop, onPlayDragChange, visible = true, collecting = false }: Props) {
  const [order, setOrder] = useState<{ identity: string; ids: string[] }>(() => ({ identity, ids: readSavedOrder(identity, hand) }));
  const [ghost, setGhost] = useState<{ tile: Tile; x: number; y: number } | null>(null);
  const root = useRef<HTMLDivElement | null>(null);
  const items = useRef(new Map<string, HTMLSpanElement>());
  const drag = useRef<Drag | null>(null);
  const suppressClick = useRef(false);
  const beforeMove = useRef(new Map<string, DOMRect>());
  const instructions = useId();
  const byId = new Map(hand.map(tile => [tile.id, tile]));
  const saved = order.identity === identity ? order.ids : [];
  const ids = [...saved.filter(id => byId.has(id)), ...hand.filter(tile => !saved.includes(tile.id)).map(tile => tile.id)];
  const handKey = hand.map(tile => tile.id).join(',');

  useEffect(() => {
    // An empty pre-play projection hides faces temporarily; keep the chosen order.
    const baseline = order.identity === identity ? drag.current?.original ?? saved : [];
    const next = [...baseline.filter(id => byId.has(id)), ...hand.filter(tile => !baseline.includes(tile.id)).map(tile => tile.id)];
    cancel();
    if (hand.length || order.identity !== identity) setOrder({ identity, ids: next });
  }, [identity, handKey]);

  useEffect(() => {
    if (order.identity !== identity) return;
    try { window.localStorage.setItem(`daaluu-hand-order:${identity}`, JSON.stringify(order.ids)); }
    catch { /* Keep hand ordering functional when storage is unavailable. */ }
  }, [identity, order]);

  useEffect(() => {
    const stop = () => cancel();
    window.addEventListener('pointerup', stop); window.addEventListener('pointercancel', stop); window.addEventListener('blur', stop);
    return () => { window.removeEventListener('pointerup', stop); window.removeEventListener('pointercancel', stop); window.removeEventListener('blur', stop); };
  }, [identity]);

  useLayoutEffect(() => {
    if (!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) for (const [id, oldRect] of beforeMove.current) {
      const element = items.current.get(id);
      if (!element || id === drag.current?.id) continue;
      const rect = element.getBoundingClientRect();
      const x = oldRect.left - rect.left, y = oldRect.top - rect.top;
      if (x || y) element.animate?.([{ transform: `translate(${x}px, ${y}px)` }, { transform: 'translate(0, 0)' }], { duration: 140, easing: 'ease-out' });
    }
    beforeMove.current.clear();
    onLayout?.();
  }, [ids.join(',')]);

  function move(next: string[]) {
    beforeMove.current = new Map([...items.current].map(([id, element]) => [id, element.getBoundingClientRect()]));
    setOrder({ identity, ids: next });
  }
  function release() {
    const current = drag.current;
    if (current?.active) onPlayDragChange?.(null);
    drag.current = null; setGhost(null);
    if (current?.element.hasPointerCapture?.(current.pointerId)) current.element.releasePointerCapture(current.pointerId);
  }
  function cancel() {
    const current = drag.current;
    if (current?.active) { setOrder({ identity, ids: current.original }); suppressClick.current = true; }
    release();
  }
  function start(event: PointerEvent<HTMLSpanElement>, id: string) {
    if (event.button !== 0 || drag.current || arriving.includes(id)) return;
    suppressClick.current = false;
    const element = root.current;
    if (!element) return;
    drag.current = { id, pointerId: event.pointerId, element, startX: event.clientX, startY: event.clientY, original: [...ids], active: false };
  }
  function pointerMove(event: PointerEvent<HTMLElement>) {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    if (!current.active && Math.hypot(event.clientX - current.startX, event.clientY - current.startY) < 8) return;
    if (!current.active) current.element.setPointerCapture?.(event.pointerId);
    if (!current.active && (onPlayDrop || onTributeDrop)) onPlayDragChange?.(current.id);
    current.active = true; suppressClick.current = true; event.preventDefault();
    const tile = byId.get(current.id);
    if (!tile) { cancel(); return; }
    setGhost({ tile, x: event.clientX, y: event.clientY });
    const handRect = root.current?.getBoundingClientRect();
    if (!handRect || event.clientX < handRect.left || event.clientX > handRect.right || event.clientY < handRect.top || event.clientY > handRect.bottom) return;
    let closest = current.id, distance = Infinity;
    for (const [id, element] of items.current) {
      const rect = element.getBoundingClientRect();
      const parent = element.offsetParent as HTMLElement | null;
      const base = parent?.getBoundingClientRect();
      // Hit-test final layout positions, not a neighbor's in-flight FLIP transform.
      const x = base && parent ? base.left + parent.clientLeft + element.offsetLeft + element.offsetWidth / 2 : rect.left + rect.width / 2;
      const y = base && parent ? base.top + parent.clientTop + element.offsetTop + element.offsetHeight / 2 : rect.top + rect.height / 2;
      const candidate = Math.hypot(event.clientX - x, event.clientY - y);
      if (candidate < distance) { closest = id; distance = candidate; }
    }
    if (closest !== current.id) {
      const next = ids.filter(id => id !== current.id);
      next.splice(ids.indexOf(closest), 0, current.id); move(next);
    }
  }
  function finish(event: PointerEvent<HTMLElement>) {
    const current = drag.current;
    if (!current || current.pointerId !== event.pointerId) return;
    const rect = root.current?.getBoundingClientRect();
    const target = document.querySelector('[data-daaluu-play-drop]')?.getBoundingClientRect();
    const tributeTarget = document.elementFromPoint?.(event.clientX, event.clientY)?.closest<HTMLElement>('[data-daaluu-tribute-target]');
    if (current.active && onTributeDrop && tributeTarget) {
      const slaveSeat = Number(tributeTarget.dataset.daaluuTributeTarget);
      if (Number.isInteger(slaveSeat)) { release(); onTributeDrop(current.id, slaveSeat); return; }
    }
    if (current.active && onPlayDrop && target &&
      event.clientX >= target.left && event.clientX <= target.right && event.clientY >= target.top && event.clientY <= target.bottom) {
      release(); onPlayDrop(current.id); return;
    }
    if (current.active && (!rect || event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) cancel();
    else release();
  }

  return <>
    <div ref={root} hidden={!visible} className="daaluu-hand" data-daaluu-motion-hand data-daaluu-hand-target={collecting ? '' : undefined} role="group" aria-label={label}
      onPointerMove={pointerMove} onPointerUp={finish} onPointerCancel={cancel}
      onLostPointerCapture={event => { if (event.target === event.currentTarget) cancel(); }}>
      <span id={instructions} className="daaluu-sr-only">{mn
        ? `Модоо чирж эрэмбэлнэ.${onPlayDrop ? ' Ширээн дээр чирж гаргана. Олон модыг эхлээд сонгоно.' : ''} Alt болон сумтай товчоор байрлалыг сольж болно.`
        : `Drag to reorder.${onPlayDrop ? ' Drag a tile to the table to play it; select multiple tiles first to drag them together.' : ''} Alt and arrow keys move a tile. Escape cancels a drag.`}</span>
      {ids.map(id => {
        const tile = byId.get(id)!;
        return <span key={id} ref={element => { if (element) items.current.set(id, element); else items.current.delete(id); }}
          data-daaluu-hand-tile={id} className={`daaluu-hand-item${arriving.includes(id) ? ' daaluu-tile-arriving' : ''}${ghost?.tile.id === id ? ' is-dragging' : ''}`}
          tabIndex={selectionDisabled || !onSelect ? 0 : -1} aria-label={daaluuKind(tile).name} aria-describedby={instructions}
          onPointerDownCapture={event => start(event, id)}
          onClickCapture={event => { if (suppressClick.current && event.detail !== 0) { event.preventDefault(); event.stopPropagation(); suppressClick.current = false; } }}
          onKeyDownCapture={event => {
            if (event.key === 'Escape' && drag.current) { event.preventDefault(); cancel(); return; }
            if (!event.altKey || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key) || arriving.includes(id)) return;
            event.preventDefault();
            const index = ids.indexOf(id), target = index + (event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1);
            if (target < 0 || target >= ids.length) return;
            const next = [...ids]; next.splice(index, 1); next.splice(target, 0, id); move(next);
          }}>
          <DaaluuTile tile={tile} selected={selected.includes(id)} disabled={selectionDisabled || arriving.includes(id)} dead={deadTileIds.includes(id)} deadLabel={mn ? 'Үхсэн' : 'Dead'} onSelect={onSelect ? () => onSelect(id) : undefined} />
        </span>;
      })}
    </div>
    {ghost && createPortal(<div className="daaluu-hand-ghost" aria-hidden="true" style={{ left: ghost.x, top: ghost.y }}>
      <DaaluuTile tile={ghost.tile} dead={deadTileIds.includes(ghost.tile.id)} deadLabel={mn ? 'Үхсэн' : 'Dead'} />
      {selected.includes(ghost.tile.id) && selected.length > 1 && <span className="daaluu-hand-ghost-count">×{selected.length}</span>}
    </div>, document.body)}
  </>;
}
